// supervisor-outcome-router.ts
//
// Routes one attempt's outcome (architecture §6.1 "on worker_closed"):
//   quota            -> quota-sleep-scheduler.enterQuotaSleep (checkpoint first)
//   worker needs_decision -> interview question (no counted failure);
//                            same question already answered -> counted contract_error (V2-0)
//   worker blocked   -> ticket blocked now, no retry (SOL-02, escalation.md)
//   done (verified + worker done) -> proposal-applier -> commit-owner -> ticket done
//   commit denied    -> journal policy_denied receipt -> escalate_waiting_human
//   else             -> retry-policy.recordAttemptOutcome (retry|blocked|escalate)
//
// Split out of supervisor-attempt-cycle.ts to keep both files under the
// 200-line budget and because routing owns its own branch-heavy logic.

import type { AutopilotResult } from "./autopilot-result-schema.js";
import type { GitRunner } from "./commit-owner.js";
import { demoteDeadlineRateLimitPastCap } from "./deadline-rate-limit-cap.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import type { QuotaConfig } from "./quota-sleep-scheduler.js";
import {
  type RetryConfig,
  type RetryDecision,
  type RetryState,
  recordAttemptOutcome,
} from "./retry-policy.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";
import { routeVerifiedDoneOutcome } from "./supervisor-outcome-router-done-path.js";
import { routeNeedsDecisionOutcome } from "./supervisor-outcome-router-needs-decision-path.js";
import { isQuotaOutcome, routeQuotaOutcome } from "./supervisor-outcome-router-quota-path.js";
import {
  attemptOutcomeFromCycle,
  blockedByWorkerDecision,
  isDoneOutcome,
  workerDeclaredOutcome,
} from "./supervisor-outcome-router-worker-outcome.js";

export type RouteOutcomeResult =
  | { kind: "done"; commitHeadSha: string | undefined }
  | { kind: "quota_sleep"; wakeAt: string }
  // B4 (phiên 2026-09-16): worker khai `needs_decision` — cần NGƯỜI quyết, không
  // phải lỗi của worker. Nhánh riêng vì mọi consumer của `retry_decision` đều
  // hiểu nó là "một lần thất bại": `counted_failures` tăng, 3 lần thành
  // `blocked` ⇒ phạt worker vì đã trung thực. Trả về id câu hỏi đã ghi để caller
  // journal/hiển thị được "đang chờ trả lời câu nào".
  | { kind: "needs_decision"; questionId: string }
  | { kind: "retry_decision"; decision: RetryDecision };

export interface RouteOutcomeInput {
  cycle: AttemptCycleResult;
  retryState: RetryState;
  retryConfig: RetryConfig;
  quotaConfig: QuotaConfig;
  quotaStatePath: string;
  /**
   * B4: control root — nơi `interview.json` sống và là root gate ĐỌC
   * (`feature-dispatch-gate-guard.ts` dùng `input.controlRoot`).
   *
   * ⚠️ KHÔNG phải `missionWorkspace`: `supervisor-loop.ts` truyền HAI root khác
   * nhau trong cùng một hàm (`controlRoot` cho gate ở :107, `missionWorkspace`
   * cho verifier ở :167). Ghi câu hỏi nhầm root ⇒ gate không bao giờ thấy nó,
   * câu hỏi chết im lặng — đúng lớp lỗi phase này tồn tại để đóng.
   */
  controlRoot: string;
  backoffIndex: number;
  now: Date;
  // Commit path.
  store: MissionStore;
  repoDir: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  manifestPaths: string[];
  missionId: string;
  runId: string;
  ticketId: string;
  ticketType: string;
  ticketScope: string;
  ticketSubject: string;
  // The CURRENT runtime status map (for proposal-applier — it needs "is this
  // dependency actually done" for cancel/create/amend).
  ticketStatus: Record<string, string>;
  // Checkpoint run BEFORE persisting SLEEPING (restores checkout).
  beforeSleep?: () => Promise<void>;
  /** R-25 (D2): operator warning line for DAG policy rules 3-4, threaded into the done path's proposal applier. */
  notify?: (line: string) => void;
  /** Consecutive deadline rate-limit quota-sleeps before this attempt (attempt-state.json). */
  deadlineRateLimitStreak?: number;
}

/** `sessionResult.result` is already `AutopilotResultSchema`-validated JSON by the time it reaches here. */
function extractProposedChanges(cycle: AttemptCycleResult): AutopilotResult["proposed_changes"] {
  if (cycle.kind !== "verified") return [];
  const result = cycle.sessionResult.result as Partial<AutopilotResult> | undefined;
  return Array.isArray(result?.proposed_changes) ? result.proposed_changes : [];
}

/** Routes exactly one attempt's outcome to its next control-loop action. */
export async function routeAttemptOutcome(
  rawInput: RouteOutcomeInput,
): Promise<RouteOutcomeResult> {
  // Review 261006 M3b: past the consecutive cap a deadline rate-limit is a timeout.
  const cycle = demoteDeadlineRateLimitPastCap(
    rawInput.cycle,
    rawInput.deadlineRateLimitStreak ?? 0,
  );
  const input = { ...rawInput, cycle };
  if (isQuotaOutcome(input.cycle)) {
    return routeQuotaOutcome({
      cycle: input.cycle,
      retryState: input.retryState,
      retryConfig: input.retryConfig,
      quotaConfig: input.quotaConfig,
      quotaStatePath: input.quotaStatePath,
      backoffIndex: input.backoffIndex,
      now: input.now,
      beforeSleep: input.beforeSleep,
    });
  }

  // B4: TRƯỚC `recordAttemptOutcome` — đi qua nó một lần là `counted_failures`
  // đã tăng, không có đường lùi. SOL-02: và TRƯỚC nhánh done — verdict "done"
  // không đọc `result.outcome`, nên needs_decision có evidence hợp lệ từng bị
  // commit thành done mà không vào interview.
  const needsDecisionRoute = await routeNeedsDecisionOutcome({
    cycle: input.cycle,
    controlRoot: input.controlRoot,
    ticketId: input.ticketId,
    now: input.now,
    retryState: input.retryState,
    retryConfig: input.retryConfig,
  });
  if (needsDecisionRoute !== undefined) return needsDecisionRoute;

  if (workerDeclaredOutcome(input.cycle) === "blocked") {
    return {
      kind: "retry_decision",
      decision: blockedByWorkerDecision(input.retryState, input.cycle),
    };
  }

  if (isDoneOutcome(input.cycle)) {
    // §6.1 "done -> proposal-applier -> commit-owner" — see
    // supervisor-outcome-router-done-path.ts for the apply/commit/deny rules.
    return routeVerifiedDoneOutcome({
      store: input.store,
      repoDir: input.repoDir,
      gitRunner: input.gitRunner,
      evaluatePolicy: input.evaluatePolicy,
      manifestPaths: input.manifestPaths,
      missionId: input.missionId,
      runId: input.runId,
      ticketId: input.ticketId,
      ticketType: input.ticketType,
      ticketScope: input.ticketScope,
      ticketSubject: input.ticketSubject,
      ticketStatus: input.ticketStatus,
      retryState: input.retryState,
      proposedChanges: extractProposedChanges(input.cycle),
      notify: input.notify,
    });
  }

  const decision = recordAttemptOutcome(
    input.retryState,
    attemptOutcomeFromCycle(input.cycle),
    input.retryConfig,
    input.now,
  );

  // A reached split point comes back as `escalate_waiting_human` (SOL-06,
  // retry-policy.ts) — no automatic child tickets are created here.
  return { kind: "retry_decision", decision };
}
